/**
 * Vendor Master form validation (UI mock — client-side).
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const MOBILE_RE = /^[6-9]\d{9}$/;
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const AADHAAR_RE = /^(\d{12}|\d{4}-\d{4}-\d{4}|XXXX-XXXX-\d{4})$/i;

export const VENDOR_FIELD_LIMITS = {
  tradeAgencyName: { min: 2, max: 100 },
  gstNumber: { max: 15 },
  ownerName: { min: 2, max: 50 },
  fatherHusbandName: { max: 50 },
  surname: { max: 50 },
  email: { max: 100 },
  mobile: { min: 10, max: 10 },
  kronosNumber: { max: 30 },
  aadhaarNumber: { max: 14 },
  pan: { max: 10 },
  address1: { max: 120 },
  address2: { max: 120 },
  pin: { min: 6, max: 6 },
};

/** Live mask: 987654321012 → 9876-5432-1012 */
export function formatAadhaarInput(raw) {
  const digits = String(raw || '')
    .replace(/\D/g, '')
    .slice(0, 12);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  if (digits.length > 8) parts.push(digits.slice(8, 12));
  return parts.join('-');
}

export function validateVendorForm(form, options = {}) {
  const { requireGst = false, requireSite = true } = options;
  const errors = {};
  const trade = String(form.tradeAgencyName || '').trim();
  const gst = String(form.gstNumber || '').trim().toUpperCase();
  const mobile = String(form.mobile || '').trim();
  const email = String(form.email || '').trim();
  const owner = String(form.ownerName || '').trim();
  const surname = String(form.surname || '').trim();
  const aadhaar = String(form.aadhaarNumber || '').trim();
  const pan = String(form.pan || '').trim().toUpperCase();
  const pin = String(form.pin || '').trim();
  const kronos = String(form.kronosNumber || '').trim();

  if (!form.buId) errors.buId = 'Select Business Unit';
  if (requireSite && !form.siteId) errors.siteId = 'Select Site';

  if (!trade) errors.tradeAgencyName = 'Trade / Agency name is required';
  else if (trade.length < VENDOR_FIELD_LIMITS.tradeAgencyName.min)
    errors.tradeAgencyName = `Min ${VENDOR_FIELD_LIMITS.tradeAgencyName.min} characters`;
  else if (trade.length > VENDOR_FIELD_LIMITS.tradeAgencyName.max)
    errors.tradeAgencyName = `Max ${VENDOR_FIELD_LIMITS.tradeAgencyName.max} characters`;

  if (requireGst && !gst) errors.gstNumber = 'GST number is required';
  else if (gst) {
    if (gst.length !== 15) errors.gstNumber = 'GST must be 15 characters';
    else if (!GST_RE.test(gst)) errors.gstNumber = 'Invalid GSTIN format';
  }

  if (!form.constitution) errors.constitution = 'Constitution is required';

  if (form.dateOfIncorporation) {
    const raw = String(form.dateOfIncorporation).trim();
    const isoOk = /^\d{4}-\d{2}-\d{2}$/.test(raw);
    if (!isoOk) errors.dateOfIncorporation = 'Enter a valid date (DD/MM/YYYY)';
    else if (raw > new Date().toISOString().slice(0, 10))
      errors.dateOfIncorporation = 'Date cannot be in the future';
  }

  if (!owner) errors.ownerName = 'Owner / Partner name is required';
  else if (owner.length < VENDOR_FIELD_LIMITS.ownerName.min)
    errors.ownerName = `Min ${VENDOR_FIELD_LIMITS.ownerName.min} characters`;

  if (surname.length > VENDOR_FIELD_LIMITS.surname.max)
    errors.surname = `Max ${VENDOR_FIELD_LIMITS.surname.max} characters`;

  if (!mobile) errors.mobile = 'Mobile is required';
  else if (!MOBILE_RE.test(mobile)) errors.mobile = 'Enter a valid 10-digit Indian mobile';

  if (!email) errors.email = 'Email is required';
  else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email address';
  else if (email.length > VENDOR_FIELD_LIMITS.email.max)
    errors.email = `Max ${VENDOR_FIELD_LIMITS.email.max} characters`;

  if (!form.villageId) errors.villageId = 'Select village from Village Master';

  if (pin && !/^\d{6}$/.test(pin)) errors.pin = 'PIN must be 6 digits';

  if (kronos.length > VENDOR_FIELD_LIMITS.kronosNumber.max)
    errors.kronosNumber = `Max ${VENDOR_FIELD_LIMITS.kronosNumber.max} characters`;

  if (aadhaar && !AADHAAR_RE.test(aadhaar))
    errors.aadhaarNumber = 'Use 12 digits (XXXX-XXXX-XXXX)';

  if (pan && !PAN_RE.test(pan)) errors.pan = 'Invalid PAN (e.g. ABCDE1234F)';

  if (String(form.address1 || '').length > VENDOR_FIELD_LIMITS.address1.max)
    errors.address1 = `Max ${VENDOR_FIELD_LIMITS.address1.max} characters`;
  if (String(form.address2 || '').length > VENDOR_FIELD_LIMITS.address2.max)
    errors.address2 = `Max ${VENDOR_FIELD_LIMITS.address2.max} characters`;

  return errors;
}
